import { useEffect, useState } from "react";
import { Scale, ClipboardList } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfig";
import { api } from "@/lib/api";
import { parseBlocks, Bullets } from "@/components/RichText";

// Renders a body string as paragraphs + bullet lists (blank line = spacing,
// a line starting with "- " becomes a bullet).
const RichBody = ({ text }) => parseBlocks(text).map((b, i) => (
  b.type === "list" ? <Bullets key={i} items={b.items} /> : <p key={i}>{b.text}</p>
));

export const BusinessRules = () => {
  const [rules, setRules] = useState([]);
  useEffect(() => { api.get("/biz-rules").then(({ data }) => setRules(data || [])).catch(() => {}); }, []);

  return (
    <div data-testid="business-rules">
      <div className="flex items-center gap-3 mb-4">
        <Scale className="h-6 w-6 text-teal" />
        <h2 className="font-display text-3xl sm:text-4xl font-light text-bone">Business Rules</h2>
      </div>
      <p className="font-body text-[15px] leading-relaxed text-[#8F877C] max-w-3xl mb-10">
        The following rules apply to owning and operating a business in Aurora Springs County. Business ownership is intended to create roleplay, employment, trade, and interaction throughout the community.
      </p>

      <div className="space-y-8 max-w-3xl">
        {rules.map((r, i) => (
          r.heading ? (
            <div key={r.id || i} data-testid={`rule-heading-${i}`} className="pt-8 mt-4 border-t border-[#2A201A]">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="h-6 w-6 text-teal" />
                <h2 className="font-display text-3xl sm:text-4xl font-light text-bone">{r.title}</h2>
              </div>
              <div className="space-y-3 text-[15px] leading-relaxed text-[#8F877C]">
                <RichBody text={r.body} />
              </div>
            </div>
          ) : (
            <div key={r.id || i} data-testid={`rule-${r.label || i}`} className="border-l-2 border-teal/40 pl-5">
              <h3 className="font-display text-xl font-light text-bone">
                {r.label && <span className="font-mono text-teal text-sm mr-2">{/^\d+$/.test(r.label) ? `${r.label}.` : r.label}</span>}
                {r.title}
              </h3>
              <div className="mt-2.5 space-y-3 text-[15px] leading-relaxed text-[#c8c0b4]">
                <RichBody text={r.body} />
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export const HowToApply = ({ className = "mt-16 border-t border-[#2A201A] pt-12" }) => {
  const { link } = useSiteConfig();
  const [steps, setSteps] = useState([]);
  useEffect(() => { api.get("/biz-apply").then(({ data }) => setSteps(data || [])).catch(() => {}); }, []);
  if (!steps.length) return null;
  const intro = steps.find((s) => s.intro);
  const list = steps.filter((s) => !s.intro);
  return (
    <div className={`max-w-3xl ${className}`} data-testid="how-to-apply">
      <div className="flex items-center gap-3 mb-4">
        <ClipboardList className="h-6 w-6 text-teal" />
        <h2 className="font-display text-2xl sm:text-3xl font-light text-bone">{intro?.title || "How to Apply for a Business"}</h2>
      </div>
      {intro?.body && (
        <div className="font-body text-[15px] leading-relaxed text-[#8F877C] mb-8 space-y-3">
          <RichBody text={intro.body} />
        </div>
      )}
      <div className="grid gap-px bg-[#2A201A]">
        {list.map((s, i) => {
          const href = s.button_url || (s.button_label ? link("countyApp") : "");
          return (
            <div key={s.id || i} className="flex gap-5 bg-obsidian p-6" data-testid={`apply-step-${i + 1}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-teal/40 bg-teal/10 font-mono text-teal">{i + 1}</span>
              <div className="flex-1">
                <p className="font-display text-lg font-light text-bone mb-2">{s.title}</p>
                <div className="space-y-2.5 text-[15px] leading-relaxed text-[#c8c0b4]"><RichBody text={s.body} /></div>
                {s.button_label && href && (
                  <a href={href} target="_blank" rel="noreferrer" data-testid={`apply-btn-${i + 1}`}
                    className="mt-3 inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2 hover:bg-bone transition-colors">
                    {s.button_label}
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
